import React, { useState } from "react";
import {
  AckErrorMessage,
  AckLoader,
  AckTable,
  AckTableRowFormatter,
  AckTableRowProps,
  convertToDisplayValue,
  formatCurrency,
  ModelSpec,
  useFetch,
} from "ackbox-weblib-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Empty, formatDecoratedCurrency, SUMMARIES_API_PATH, useEffectOnce } from "./Constants";
import { Progress, useProgress } from "./Progress";

const HistorySummary = () => {
  const [data, setData] = useState([] as any[]);
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    const value = await ackFetch.get("history/");
    setData(value);
  });

  const Chart = () => {
    const formatter = (value: any) => formatCurrency(value);
    return (
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3"/>
            <XAxis dataKey="id" angle={-45} height={60} tickMargin={20} tick={{ fontSize: 10 }}/>
            <YAxis tickCount={6} interval={0} tick={{ fontSize: 12 }} tickFormatter={formatter}/>
            <Tooltip formatter={formatter}/>
            <Legend layout="horizontal"/>
            <Bar dataKey="incomes" opacity="0.75" stroke="#f1c40f" fill="#f1c40f"/>
            <Bar dataKey="expenses" opacity="0.75" stroke="#d35400" fill="#d35400"/>
            <Bar dataKey="total" opacity="0.75" stroke="#1abc9c" fill="#1abc9c"/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <>
      <h1 className="subtitle">History</h1>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      {!isLoading && data.length === 0 && <Empty/>}
      {!isLoading && data.length > 0 && <Chart/>}
    </>
  );
};

const NetWorthSummary = () => {
  const [data, setData] = useState([] as any[]);
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    const value = await ackFetch.get("net-worth/");
    setData(value);
  });

  const Chart = () => {
    const formatter = (value: any) => formatCurrency(value);
    return (
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 30, bottom: 10 }}>
            <CartesianGrid strokeDasharray="2 2"/>
            <XAxis dataKey="id" angle={-45} height={60} interval={1} tickMargin={20} tick={{ fontSize: 10 }}/>
            <YAxis tickCount={6} interval={0} tick={{ fontSize: 12 }} tickFormatter={formatter}/>
            <Tooltip formatter={formatter}/>
            <Area dataKey="total" type="monotone" stroke="#1abc9c" fill="#1abc9c"/>
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <>
      <h1 className="subtitle">Net Worth</h1>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      {!isLoading && data.length === 0 && <Empty/>}
      {!isLoading && data.length > 0 && <Chart/>}
    </>
  );
};

const FundsSummary = () => {
  const [rows, setRows] = useState([] as AckTableRowProps[]);
  const [isEmpty, setIsEmpty] = useState(true);
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    const value = await ackFetch.get("funds/");
    const spec: ModelSpec[] = [
      { key: "incomes", label: "Incomes", name: "incomes", type: "currency" },
      { key: "expenses", label: "Expenses", name: "expenses", type: "currency" },
      { key: "transfersIn", label: "Transfers In", name: "transfersIn", type: "currency" },
      { key: "transfersOut", label: "Transfers Out", name: "transfersOut", type: "currency" },
      { key: "total", label: "Total", name: "total", type: "currency" },
    ];
    const rows: AckTableRowProps[] = [{ spec: spec, payload: value }];

    setRows(rows);
    setIsEmpty(value.incomes === 0 && value.expenses === 0 && value.transfersIn === 0 && value.transfersOut === 0);
  });

  const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
    if (spec.name === "total") {
      return (<strong>{formatDecoratedCurrency(payload[spec.name])}</strong>);
    }
    return convertToDisplayValue(spec, payload);
  };

  return (
    <>
      <h1 className="subtitle">Overall Funds</h1>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      {!isLoading && isEmpty && <Empty/>}
      {!isLoading && !isEmpty && <AckTable rows={rows} formatters={formatters} hideIndexCol={true}/>}
    </>
  );
};

const AccountSummary = () => {
  const [rows, setRows] = useState([] as AckTableRowProps[]);
  const ackFetch = useFetch(SUMMARIES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    const values = await ackFetch.get("account-transactions/");
    const spec: ModelSpec[] = [
      { key: "name", label: "Name", name: "id", type: "string" },
      { key: "incomes", label: "Incomes", name: "incomes", type: "currency" },
      { key: "expenses", label: "Expenses", name: "expenses", type: "currency" },
      { key: "transfersIn", label: "Transfers In", name: "transfersIn", type: "currency" },
      { key: "transfersOut", label: "Transfers Out", name: "transfersOut", type: "currency" },
      { key: "total", label: "Total", name: "total", type: "currency" },
    ];
    const rows: AckTableRowProps[] = values.filter((value: any) => value.total > 0.001)
      .map((payload: any) => ({ spec: spec, payload: payload }));

    setRows(rows);
  });
  const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
    if (spec.name === "total") {
      return (<strong>{formatDecoratedCurrency(payload[spec.name])}</strong>);
    }
    return convertToDisplayValue(spec, payload);
  };

  return (
    <>
      <h1 className="subtitle">Accounts</h1>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      {!isLoading && rows.length === 0 && <Empty/>}
      {!isLoading && rows.length > 0 && <AckTable rows={rows} formatters={formatters}/>}
    </>
  );
};

export const OverallSummary = () => {
  const progress = useProgress();
  return (
    <>
      <div className="section">
        <h1 className="title is-spaced">Overall Summary</h1>
        {!progress.isComplete && <Progress {...progress}/>}
        {progress.isComplete && <HistorySummary/>}
        {progress.isComplete && <NetWorthSummary/>}
        {progress.isComplete && <FundsSummary/>}
        {progress.isComplete && <AccountSummary/>}
      </div>
    </>
  );
};
